/**
 * Web CV Health score card: the twin of `HealthScoreCard.tsx`.
 *
 * The glass card of the design: optionally the heart with "CV Health" at the
 * top, the gauge, then either the capsule action ("Review 5 suggestions") or,
 * when nothing is left to review, "All checks passed". No strength badge, no
 * "out of 100" heading, no generic copy. A report without a score (no
 * eligible checks) says "Couldn't check" in the gauge's place; a partial one
 * adds "Some checks couldn't finish."
 */
import { useTranslation } from 'react-i18next';
import { countSuggestions } from '@/lib/cv-health-model';
import { HealthHeart } from './HealthHeart';
import { HealthScoreGauge } from './HealthScoreGauge';
import type { HealthScoreCardProps } from './health-props';

export type { HealthScoreCardProps };

export function HealthScoreCard({
  report,
  showIdentity = false,
  action,
  historyKey,
  animate = true,
  fromScore,
}: HealthScoreCardProps) {
  const { t } = useTranslation('common');
  const hasScore = typeof report.score === 'number';
  const clean = countSuggestions(report) === 0;

  return (
    <section className="tabbio-health tabbio-health-card" aria-label={t('cvHealth.title')}>
      {showIdentity ? (
        <div className="tabbio-health-identity">
          <HealthHeart size={20} state="resting" />
          <h2>{t('cvHealth.title')}</h2>
        </div>
      ) : null}
      {hasScore ? (
        <HealthScoreGauge
          score={report.score as number}
          historyKey={historyKey}
          animate={animate}
          fromScore={fromScore}
        />
      ) : (
        <p className="tabbio-health-card-result">{t('cvHealth.report.couldntCheck')}</p>
      )}
      {action ? (
        <button
          type="button"
          className="tabbio-health-card-action"
          onClick={action.onPress}
          disabled={action.disabled || action.busy}
          aria-busy={action.busy || undefined}
        >
          {action.label}
        </button>
      ) : hasScore && clean ? (
        <p className="tabbio-health-card-result">{t('cvHealth.report.allPassed')}</p>
      ) : null}
      {report.status === 'partial' ? (
        <p className="tabbio-health-card-note">{t('cvHealth.heading.partial')}</p>
      ) : null}
    </section>
  );
}

export default HealthScoreCard;
